import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Globe, Heart, User, ChevronRight, Sparkles } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface FavoritesOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSetNation: () => void;
  onSetTeams: () => void;
  onSetPlayers: () => void;
  hasNation: boolean;
  hasTeams: boolean;
  hasPlayers: boolean;
}

export default function FavoritesOnboardingModal({
  isOpen, onClose, onSetNation, onSetTeams, onSetPlayers,
  hasNation, hasTeams, hasPlayers,
}: FavoritesOnboardingModalProps) {
  const isMobile = useIsMobile();

  const steps = [
    { icon: Globe, label: "Favorite Nation", desc: "Get country-specific games & alerts", done: hasNation, action: onSetNation },
    { icon: Heart, label: "Favorite Teams", desc: "Follow leagues & teams for live scores", done: hasTeams, action: onSetTeams },
    { icon: User, label: "Favorite Players", desc: "Track player stats across leagues", done: hasPlayers, action: onSetPlayers },
  ];

  const allDone = hasNation && hasTeams && hasPlayers;

  const handleAction = (action: () => void) => {
    onClose();
    action();
  };

  const Content = () => (
    <div className="flex flex-col items-center text-center py-4">
      <div className="relative mb-4">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
          <Sparkles className="w-8 h-8 text-primary" />
        </div>
      </div>

      <h3 className="font-display text-xl font-bold text-foreground mb-2">
        Set Up Your Hub
      </h3>
      <p className="text-muted-foreground mb-6 max-w-sm text-sm">
        Personalize your dashboard by choosing your favorites. Complete any step to get started.
      </p>

      <div className="w-full space-y-3">
        {steps.map((step) => (
          <button
            key={step.label}
            onClick={() => handleAction(step.action)}
            disabled={step.done}
            className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
              step.done 
                ? "border-primary/30 bg-primary/5 opacity-60" 
                : "border-border hover:border-primary/30 hover:bg-primary/5"
            }`}
          >
            <div className={`p-2 rounded-lg ${step.done ? 'bg-primary/20' : 'bg-secondary'}`}>
              <step.icon className={`w-5 h-5 ${step.done ? 'text-primary' : 'text-muted-foreground'}`} />
            </div>
            <div className="flex-1">
              <p className="font-medium text-foreground text-sm">{step.label}</p>
              <p className="text-xs text-muted-foreground">{step.desc}</p>
            </div>
            {step.done ? (
              <span className="text-xs text-primary font-medium">✓ Done</span>
            ) : (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            )}
          </button>
        ))}
      </div>

      <Button onClick={onClose} variant="ghost" className="mt-4 w-full text-muted-foreground">
        {allDone ? "Done" : "I'll do this later"}
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="px-4 pb-6">
          <DrawerHeader className="text-center">
            <DrawerTitle className="sr-only">Set Up Your Hub</DrawerTitle>
            <DrawerDescription className="sr-only">Choose your favorite nation, teams and players</DrawerDescription>
          </DrawerHeader>
          <Content />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="sr-only">Set Up Your Hub</DialogTitle>
          <DialogDescription className="sr-only">Choose your favorite nation, teams and players</DialogDescription>
        </DialogHeader>
        <Content />
      </DialogContent>
    </Dialog>
  );
}
